import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  SxProps,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import { ReactNode, useContext, useMemo, } from "react";
import { AUTURA_ASPHALT_LIGHT } from "context/themeContext";
import { capitalizeFirstLetter } from "utils/stringFormatters";
import LinkIcon from "@mui/icons-material/Link";
import copy from "copy-to-clipboard";
import { TOAST_TYPE, ToastContext } from "context/toastContext";

export interface BooleanSelectorProps {
  name: string;
  disabled?: boolean;
  readonly?: boolean;
  value: boolean;
  label?: string;
  setValue?: (value: boolean) => void;
  sx?: SxProps;
  helperText?: string;
  link?: string;
}

const AuturaSwitch = ({
  name,
  disabled,
  label = "",
  helperText,
  readonly,
  value,
  setValue,
  sx,
  link = "",
}: BooleanSelectorProps): ReactNode => {
  const { palette } = useTheme();
  const displayToast = useContext(ToastContext);
  const handleChange = () => {
    if (setValue) setValue(!value);
  };
  
  const ControlLabel = useMemo(() => {
    if (!link || disabled) {
      return label;
    }

    return (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%" gap={2}>
          {label}
          <Tooltip title="Copy link to clipboard">
            <LinkIcon sx={{ m: 0.5, fontSize: 28}} onClick={(e: React.MouseEvent<SVGSVGElement>) => {
              e.preventDefault();
              e.stopPropagation();
              copy(link);
              displayToast({
                type: TOAST_TYPE.SUCCESS,
                message: "Link copied to clipboard",
              });
            }} />
          </Tooltip>
        </Box>
    );
  }, [label, link]);


  return (
    <Stack
      direction={"column"}
      display={"flex"}
      justifyContent={"flex-start"}
      alignContent={"flex-start"}
      sx={{ height: 60, pl: 1.25, ...sx }}
    >
      <FormControlLabel
        sx={{ height: 50 }}
        label={ControlLabel}
        disabled={disabled}
        control={
          <Box
            sx={{
              width: "60px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Switch
              checked={value}
              name={name}
              readOnly={readonly}
              disabled={disabled}
              color={"primary"}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: palette.primary.main,
                },
                "& .MuiSwitch-track": {
                  backgroundColor: disabled
                    ? AUTURA_ASPHALT_LIGHT
                    : value
                      ? palette.primary.main
                      : AUTURA_ASPHALT_LIGHT,
                },
                "& .MuiSwitch-thumb": {
                  backgroundColor: disabled
                    ? AUTURA_ASPHALT_LIGHT
                    : value
                      ? palette.primary.main
                      : AUTURA_ASPHALT_LIGHT,
                },
              }}
              onChange={handleChange}
            />
          </Box>
        }
      />
      <Typography
        variant="caption"
        ml={"50px"}
        lineHeight={1}
        sx={{
          mt: -1,
          opacity: disabled ? 0.5 : 0.8,
        }}
      >
        {typeof helperText === 'string' ? capitalizeFirstLetter(helperText) : helperText || ''}
      </Typography>
    </Stack>
  );
};

export default AuturaSwitch;
